const MODAL_HISTORY_STATE_KEY = '__rbModalHistoryEntry';

interface ModalHistoryEntry {
  id: string;
  onBack: () => void;
  hasHistoryEntry: boolean;
}

interface PendingHistoryBack {
  id: string;
}

let nextModalHistoryId = 0;
let activeModals: ModalHistoryEntry[] = [];
let pendingHistoryBack: PendingHistoryBack | null = null;
let isPopStateListenerAttached = false;

const getCurrentModalHistoryId = (): string | null => {
  if (typeof window === 'undefined') {
    return null;
  }

  const historyState = window.history.state as Record<string, unknown> | null;
  const modalHistoryId = historyState?.[MODAL_HISTORY_STATE_KEY];

  return typeof modalHistoryId === 'string' ? modalHistoryId : null;
};

const pushModalHistoryEntry = (entry: ModalHistoryEntry): void => {
  if (typeof window === 'undefined') {
    return;
  }

  if (getCurrentModalHistoryId() === entry.id) {
    entry.hasHistoryEntry = true;
    return;
  }

  const currentState = window.history.state as Record<string, unknown> | null;
  const nextState =
    currentState != null && typeof currentState === 'object'
      ? { ...currentState, [MODAL_HISTORY_STATE_KEY]: entry.id }
      : { [MODAL_HISTORY_STATE_KEY]: entry.id };

  window.history.pushState(nextState, '');
  entry.hasHistoryEntry = true;
};

const pushDeferredModalHistoryEntries = (): void => {
  activeModals.forEach((entry) => {
    if (!entry.hasHistoryEntry) {
      pushModalHistoryEntry(entry);
    }
  });
};

const discardStaleCurrentHistoryEntry = (): void => {
  if (typeof window === 'undefined' || pendingHistoryBack) {
    return;
  }

  const currentModalHistoryId = getCurrentModalHistoryId();

  if (!currentModalHistoryId) {
    return;
  }

  const currentModal = activeModals.find((entry) => entry.id === currentModalHistoryId);

  if (currentModal) {
    currentModal.hasHistoryEntry = true;
    return;
  }

  pendingHistoryBack = { id: currentModalHistoryId };
  window.history.back();
};

const handlePopState = (): void => {
  if (pendingHistoryBack) {
    pendingHistoryBack = null;
    pushDeferredModalHistoryEntries();
    discardStaleCurrentHistoryEntry();
    return;
  }

  const topModal = activeModals.at(-1);

  if (!topModal) {
    return;
  }

  activeModals = activeModals.filter((entry) => entry.id !== topModal.id);
  topModal.hasHistoryEntry = false;
  topModal.onBack();

  // A nested modal can leave the hidden parent's same-page history entry
  // underneath it. Give React a chance to reveal/register the parent again;
  // otherwise discard that stale entry without navigating away from the page.
  window.setTimeout(discardStaleCurrentHistoryEntry, 0);
};

const attachPopStateListener = (): void => {
  if (typeof window === 'undefined' || isPopStateListenerAttached) {
    return;
  }

  window.addEventListener('popstate', handlePopState);
  isPopStateListenerAttached = true;
};

export const createModalHistoryId = (): string => {
  nextModalHistoryId += 1;
  return `rb-modal-${nextModalHistoryId}`;
};

export const registerModalHistory = (id: string, onBack: () => void): void => {
  attachPopStateListener();

  const existingEntry = activeModals.find((entry) => entry.id === id);

  if (existingEntry) {
    existingEntry.onBack = onBack;
    return;
  }

  const entry: ModalHistoryEntry = {
    id,
    onBack,
    hasHistoryEntry: false,
  };

  activeModals.push(entry);

  // A history.back() started while another modal was closing is asynchronous.
  // Wait for its popstate before pushing this modal, otherwise that back call
  // could accidentally consume the new modal's entry.
  if (!pendingHistoryBack) {
    pushModalHistoryEntry(entry);
  }
};

export const unregisterModalHistory = (id: string): void => {
  const entry = activeModals.find((activeEntry) => activeEntry.id === id);

  if (!entry) {
    return;
  }

  activeModals = activeModals.filter((activeEntry) => activeEntry.id !== id);

  if (
    typeof window === 'undefined' ||
    !entry.hasHistoryEntry ||
    getCurrentModalHistoryId() !== id ||
    pendingHistoryBack
  ) {
    return;
  }

  entry.hasHistoryEntry = false;
  pendingHistoryBack = { id };
  window.history.back();
};
